import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_BANNER_MUTATION, UPDATE_BANNER_MUTATION } from '../../apollo/queries';
import { uploadFileToS3, validateFile } from '../../utils/s3Upload';
import '../css/BannerModal.css';

const BannerModal = ({ isOpen, onClose, banner = null, onSuccess }) => {
    const fileInputRef = useRef(null);
    const isEditing = !!banner;

    // GraphQL mutations
    const [createBanner] = useMutation(CREATE_BANNER_MUTATION, {
        onCompleted: () => {
            alert('배너가 성공적으로 저장되었습니다.');
            onClose();
        },
        onError: (error) => {
            alert(`배너 저장 실패: ${error.message}`);
        }
    });

    const [updateBanner] = useMutation(UPDATE_BANNER_MUTATION, {
        onCompleted: () => {
            alert('배너가 성공적으로 수정되었습니다.');
            onClose();
        },
        onError: (error) => {
            alert(`배너 수정 실패: ${error.message}`);
        }
    });

    // 폼 상태 관리 (간단화)
    const [formData, setFormData] = useState({
        position: '첫번째',
        linkUrl: ''
    });

    // 파일 관련 상태
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('선택한 파일 없음');
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    // 편집 모드일 때 기존 데이터로 초기화
    useEffect(() => {
        if (isEditing && banner) {
            setFormData({
                position: banner.position || '첫번째',
                linkUrl: banner.linkUrl || ''
            });
            setUploadedImageUrl(banner.imageUrl || '');
            setPreviewUrl(banner.imageUrl || '');
            setFileName(banner.imageUrl ? '기존 이미지' : '선택한 파일 없음');
        } else {
            // 새로 추가할 때 초기화
            setFormData({
                position: '첫번째',
                linkUrl: ''
            });
            setSelectedFile(null);
            setFileName('선택한 파일 없음');
            setUploadedImageUrl('');
            setPreviewUrl('');
        }
    }, [isEditing, banner, isOpen]);

    // 입력값 변경 처리
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 파일 선택 처리
    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // 파일 유효성 검사
            validateFile(file);
            
            setSelectedFile(file);
            setFileName(file.name);
            
            // 미리보기 URL 생성
            const previewURL = URL.createObjectURL(file);
            setPreviewUrl(previewURL);
            
            // S3 업로드
            setIsUploading(true);
            const uploadedUrl = await uploadFileToS3(file, 'banners');
            setUploadedImageUrl(uploadedUrl);
            
        } catch (error) {
            alert(`파일 처리 오류: ${error.message}`);
            setSelectedFile(null);
            setFileName('선택한 파일 없음');
            setPreviewUrl('');
        } finally {
            setIsUploading(false);
        }
    };

    // 이미지 제거
    const handleRemoveImage = () => {
        setSelectedFile(null);
        setFileName('선택한 파일 없음');
        setUploadedImageUrl('');
        setPreviewUrl('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 폼 제출
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!uploadedImageUrl) {
            alert('이미지를 업로드해주세요.');
            return;
        }

        const bannerData = {
            position: formData.position,
            imageUrl: uploadedImageUrl,
            linkUrl: formData.linkUrl,
            width: null,
            height: null,
            backgroundColor: null,
            bannerText: null,
            order: 0,
            isVisible: true
        };

        try {
            if (isEditing) {
                await updateBanner({
                    variables: {
                        id: banner.id,
                        input: bannerData
                    }
                });
            } else {
                await createBanner({
                    variables: {
                        input: bannerData
                    }
                });
            }
        } catch (error) {
            // 에러는 onError 콜백에서 처리됨
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content banner-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEditing ? '배너 수정' : '배너 추가'}</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                
                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="form-row">
                        <label>노출 위치</label>
                        <select 
                            name="position" 
                            value={formData.position} 
                            onChange={handleInputChange}
                            required
                        >
                            <option value="첫번째">첫번째</option>
                            <option value="두번째">두번째</option>
                            <option value="세번째">세번째</option>
                            <option value="네번째">네번째</option>
                            <option value="하단">하단</option>
                        </select>
                    </div>

                    <div className="form-row">
                        <label>배너 파일</label>
                        <div className="file-input-group">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                            <button 
                                type="button"
                                className="btn-file-select" 
                                onClick={handleFileSelect}
                                disabled={isUploading}
                            >
                                {isUploading ? '업로드 중...' : '파일 선택'}
                            </button>
                            <span className="file-name">{fileName}</span>
                            
                            {uploadedImageUrl && (
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="btn-remove-image"
                                >
                                    제거
                                </button>
                            )}
                        </div>
                        
                        {uploadedImageUrl && (
                            <div className="upload-status">
                                <span className="success-text">✓ 업로드 완료</span>
                                <a 
                                    href={uploadedImageUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="s3-link"
                                >
                                    S3 링크 보기
                                </a>
                            </div>
                        )}
                    </div>

                    <div className="form-row">
                        <label>링크 URL</label>
                        <input
                            type="url"
                            name="linkUrl"
                            value={formData.linkUrl}
                            onChange={handleInputChange}
                            placeholder="https://example.com (선택사항)"
                            style={{
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                width: '100%'
                            }}
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            배너 클릭 시 새 탭에서 열릴 링크 주소입니다.
                        </small>
                    </div>

                    {previewUrl && (
                        <div className="form-row">
                            <label>미리보기</label>
                            <div className="image-preview">
                                <img src={previewUrl} alt="미리보기" />
                            </div>
                        </div>
                    )}

                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="btn-cancel">
                            취소
                        </button>
                        <button type="submit" className="btn-submit" disabled={isUploading}>
                            {isEditing ? '수정' : '저장'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BannerModal; 